import {
  fetchBoard,
  type BoardFetchError,
} from '../api/boardsClient.js';
import {
  getPage,
  ProjectsClientError,
} from '../api/projectsClient.js';
import type { NodeRoute } from './nodeRoute.js';
import {
  ProjectSurfaceLoadError,
  type ProjectSurfaceDescriptor,
} from './projectShellRouter.js';

export interface ProjectSurfaceDescriptorDependencies {
  fetchBoard: typeof fetchBoard;
  getPage: typeof getPage;
}

const defaultDependencies: ProjectSurfaceDescriptorDependencies = {
  fetchBoard,
  getPage,
};

function mapBoardSurfaceError(error: BoardFetchError): ProjectSurfaceLoadError {
  if (error.kind === 'unauthorized') return new ProjectSurfaceLoadError('unauthorized', 401);
  if (error.kind === 'forbidden') return new ProjectSurfaceLoadError('forbidden', 403);
  if (error.kind === 'not_found') return new ProjectSurfaceLoadError('not_found', 404);
  if (error.kind === 'server') return new ProjectSurfaceLoadError('server', error.status);
  if (error.kind === 'network') return new ProjectSurfaceLoadError('network');
  return new ProjectSurfaceLoadError('malformed');
}

function mapPageSurfaceError(error: unknown): ProjectSurfaceLoadError {
  if (!(error instanceof ProjectsClientError)) return new ProjectSurfaceLoadError('network');
  if (error.status === 401) return new ProjectSurfaceLoadError('unauthorized', 401);
  if (error.status === 403) return new ProjectSurfaceLoadError('forbidden', 403);
  if (error.status === 404) return new ProjectSurfaceLoadError('not_found', 404);
  if (error.status === 0) return new ProjectSurfaceLoadError('network');
  return new ProjectSurfaceLoadError('server', error.status);
}

export async function resolveProjectSurfaceDescriptor(
  route: NodeRoute,
  signal: AbortSignal,
  apiBase: string,
  dependencies: ProjectSurfaceDescriptorDependencies = defaultDependencies,
): Promise<ProjectSurfaceDescriptor> {
  if (route.kind === 'page') {
    try {
      const page = await dependencies.getPage(apiBase, route.id, { signal });
      return {
        kind: 'page',
        route,
        projectId: page.project_id,
        title: page.title,
        payload: page,
      };
    } catch (error) {
      throw mapPageSurfaceError(error);
    }
  }
  const outcome = await dependencies.fetchBoard(apiBase, route.id, { signal });
  if (!outcome.ok) throw mapBoardSurfaceError(outcome.error);
  if (outcome.result.board.project_id === undefined) {
    throw new ProjectSurfaceLoadError('malformed');
  }
  return {
    kind: 'board',
    route,
    projectId: outcome.result.board.project_id,
    title: outcome.result.board.title,
    payload: outcome.result,
  };
}
